<template>
  <div v-on-clickaway="onClose" class="labels-container">
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path
          d="M20 12l-8-8-12 12"
          fill="var(--white)"
          fill-rule="evenodd"
          stroke="var(--s-50)"
          stroke-width="1px"
        />
      </svg>
    </div>
    <div class="header flex-between">
      <span>{{ panelTitle }}</span>
      <woot-button
        size="tiny"
        variant="clear"
        color-scheme="secondary"
        icon="dismiss"
        @click="onClose"
      />
    </div>
    <div v-if="!isAttributesMode" class="labels-list">
      <header class="labels-list__header">
        <div class="label-list-search flex-between">
          <fluent-icon icon="search" class="search-icon" size="16" />
          <input
            ref="search"
            v-model="query"
            type="search"
            placeholder="Pesquisar marcador"
            class="label--search_input"
          />
        </div>
      </header>
      <ul class="labels-list__body">
        <li
          v-for="label in filteredLabels"
          :key="label.id"
          class="label__list-item"
        >
          <label
            class="item"
            :class="{ 'label-selected': isLabelSelected(label.title) }"
          >
            <input
              v-model="selectedLabels"
              type="checkbox"
              :value="label.title"
              class="label-checkbox"
            />
            <span class="label-title text-truncate">{{ label.title }}</span>
            <span
              class="label-pill"
              :style="{ backgroundColor: label.color }"
            />
          </label>
        </li>
      </ul>
      <footer class="labels-list__footer">
        <div class="labels-list__footer-actions">
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="alert"
            :disabled="!selectedLabels.length"
            @click="emitAction('remove')"
          >
            <span>{{ $t('BULK_ACTION.CRM.REMOVE_LABELS') }}</span>
          </woot-button>
          <woot-button
            size="small"
            color-scheme="primary"
            :disabled="!selectedLabels.length"
            @click="emitAction('add')"
          >
            <span>{{ $t('BULK_ACTION.LABELS.ASSIGN_SELECTED_MARKERS') }}</span>
          </woot-button>
        </div>
      </footer>
    </div>
    <div v-else class="labels-list">
      <div class="labels-list__body labels-list__body--form">
        <label class="field-label">
          <select v-model="selectedAttributeKey" class="label-input-select">
            <option :value="''" disabled>
              {{ $t('BULK_ACTION.CRM.CHOOSE_ATTRIBUTE') }}
            </option>
            <option
              v-for="attribute in ticketCustomAttributes"
              :key="attribute.id"
              :value="attribute.attribute_key"
            >
              {{ attribute.attribute_display_name }}
            </option>
          </select>
        </label>

        <span
          v-if="!ticketCustomAttributes.length"
          class="labels-list__empty-hint"
        >
          {{ $t('BULK_ACTION.CRM.NO_CONTACT_ATTRIBUTES_AVAILABLE') }}
        </span>

        <label class="field-label">
          {{ $t('BULK_ACTION.CRM.VALUE') }}
          <input
            v-model.trim="attributeValue"
            class="label-input-text"
            :placeholder="$t('BULK_ACTION.CRM.VALUE_PLACEHOLDER')"
          />
        </label>
      </div>
      <footer class="labels-list__footer">
        <div class="labels-list__footer-actions">
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="alert"
            :disabled="!selectedAttributeKey"
            @click="emitAttributeAction('remove')"
          >
            <span>{{ $t('BULK_ACTION.CRM.REMOVE_ATTRIBUTES') }}</span>
          </woot-button>
          <woot-button
            size="small"
            color-scheme="primary"
            :disabled="!canSubmitAttributes"
            @click="emitAttributeAction('add')"
          >
            <span>{{ $t('BULK_ACTION.CRM.ADD_ATTRIBUTE') }}</span>
          </woot-button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';

export default {
  mixins: [clickaway],
  props: {
    mode: {
      type: String,
      default: 'labels',
    },
    useContactLabels: {
      type: Boolean,
      default: false,
    },
    ticketCustomAttributes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      query: '',
      selectedLabels: [],
      selectedAttributeKey: '',
      attributeValue: '',
    };
  },
  computed: {
    ...mapGetters({
      crmLabels: 'labelsCrm/getLabels',
      contactLabels: 'labels/getLabels',
    }),
    labels() {
      return this.useContactLabels ? this.contactLabels : this.crmLabels;
    },
    isAttributesMode() {
      return this.mode === 'attributes';
    },
    panelTitle() {
      return this.isAttributesMode
        ? this.$t('BULK_ACTION.CRM.CONTACT_ATTRIBUTES_PANEL_TITLE')
        : this.$t('BULK_ACTION.LABELS.ASSIGN_MARKERS');
    },
    filteredLabels() {
      return this.labels.filter(label =>
        label.title.toLowerCase().includes(this.query.toLowerCase())
      );
    },
    canSubmitAttributes() {
      if (!this.selectedAttributeKey) {
        return false;
      }
      return this.attributeValue !== '';
    },
  },
  methods: {
    isLabelSelected(label) {
      return this.selectedLabels.includes(label);
    },
    emitAction(actionType) {
      this.$emit('assign', {
        labels: this.selectedLabels,
        actionType,
      });
    },
    emitAttributeAction(actionType = 'add') {
      this.$emit('assign-custom-attributes', {
        key: this.selectedAttributeKey,
        value: actionType === 'remove' ? '' : this.attributeValue,
        actionType,
      });
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.labels-list {
  display: flex;
  flex-direction: column;
  max-height: var(--space-giga);
  min-height: auto;

  .labels-list__header {
    background-color: var(--white);
    padding: 0 var(--space-one);
  }

  .labels-list__body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-one) 0;
  }

  .labels-list__footer {
    padding: var(--space-small);
    display: flex;
    justify-content: center;
    align-items: center;
    button {
      width: 100%;
    }
  }

  .labels-list__footer-actions {
    display: flex;
    flex-direction: column-reverse;
    gap: var(--space-small);
  width: 80% !important;

  }

  .labels-list__body--form {
    display: flex;
    flex-direction: column;
    gap: var(--space-small);
    padding: var(--space-small);
  }
}

.field-label {
  color: var(--s-700);
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  gap: var(--space-micro);
}

.label-input-select,
.label-input-text {
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-medium);
  font-size: var(--font-size-small);
  padding: var(--space-small);
}

.labels-list__empty-hint {
  color: var(--s-500);
  font-size: var(--font-size-mini);
}

.label-list-search {
  background-color: var(--s-50);
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--s-100);
  padding: 0 var(--space-one);

  .search-icon {
    color: var(--s-400);
  }

  .label--search_input {
    background-color: transparent;
    border: 0;
    font-size: var(--font-size-mini);
    height: unset;
    margin: 0;
  }
}

.labels-container {
  background-color: var(--white);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--s-50);
  box-shadow: var(--shadow-dropdown-pane);
  max-width: var(--space-giga);
  min-width: var(--space-giga);
  position: absolute;
  right: var(--space-small);
  top: var(--space-larger);
  transform-origin: top right;
  width: auto;
  z-index: var(--z-index-twenty);

  .header {
    padding: var(--space-one);

    span {
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-medium);
    }
  }

  .container {
    max-height: var(--space-giga);
    overflow-y: auto;

    .label__list-container {
      height: 100%;
    }
    .label-list-search {
      padding: 0 var(--space-one);
      border: 1px solid var(--s-100);
      border-radius: var(--border-radius-medium);
      background-color: var(--s-50);
      .search-icon {
        color: var(--s-400);
      }

      .label--search_input {
        border: 0;
        font-size: var(--font-size-mini);
        margin: 0;
        background-color: transparent;
        height: unset;
      }
    }
  }

  .triangle {
    display: block;
    position: absolute;
    right: var(--triangle-position);
    text-align: left;
    top: var(--space-minus-slab);
    z-index: var(--z-index-one);
  }
}

ul {
  margin: 0;
  list-style: none;
}

.labels-placeholder {
  padding: var(--space-small);
}

.label__list-item {
  margin: var(--space-smaller) 0;
  padding: 0 var(--space-one);

  .item {
    align-items: center;
    border-radius: var(--border-radius-medium);
    cursor: pointer;
    display: flex;
    padding: var(--space-smaller) var(--space-one);

    &:hover {
      background-color: var(--s-50);
    }

    &.label-selected {
      background-color: var(--s-50);
    }

    span {
      font-size: var(--font-size-small);
    }

    .label-checkbox {
      margin: 0 var(--space-one) 0 0;
    }

    .label-title {
      flex-grow: 1;
      width: 100%;
    }

    .label-pill {
      background-color: var(--s-50);
      border-radius: var(--border-radius-medium);
      height: var(--space-slab);
      width: var(--space-slab);
      flex-shrink: 0;
      border: 1px solid var(--color-border-light);
    }
  }
}

.search-container {
  background-color: var(--white);
  padding: 0 var(--space-one);
  position: sticky;
  top: 0;
  z-index: var(--z-index-twenty);
}

.actions-container {
  background-color: var(--white);
  bottom: 0;
  padding: var(--space-small);
  position: sticky;
  z-index: var(--z-index-twenty);

  button {
    width: 100%;
  }
}
</style>
